export interface FAQQuestion {
  question: string;
  answer: string;
}

export const generalQuestions: FAQQuestion[] = [
  {
    question: "What is a worker cooperative?",
    answer:
      "A worker cooperative (or worker co-op) is a business owned and democratically controlled by the people who work there. Unlike traditional companies, where decisions are made by executives and shareholders, co-ops give every worker an equal voice in major decisions and a share of the profits.",
  },
  {
    question: "How is EconChangers different from other crowdfunding platforms?",
    answer:
      "EconChangers focuses exclusively on worker-owned businesses. We combine crowdfunding and growth campaigns (FlockTank) with business operations software (FlockWorker) and a service marketplace (FlockList) to provide comprehensive support. FlockTank supports launch campaigns for new co-ops, transition campaigns for business conversions, and growth campaigns for established cooperatives looking to scale. We also require all funded co-ops to commit to worker-first policies like the 50/50 profit split.",
  },
  {
    question: "What are EconChangers Co-Ops?",
    answer:
      "EconChangers Co-Ops are worker cooperatives that have been funded through FlockTank and agree to our core policies: a 50/50 profit split with workers, job security commitments, and public financial transparency. They also use FlockWorker for their business operations.",
  },
];

export const investorQuestions: FAQQuestion[] = [
  {
    question: "How do I invest in a co-op through FlockTank?",
    answer:
      "Simply create a free account, browse available launch, transition, or growth campaigns, and invest in projects that align with your values. You can invest as little as $100 in most campaigns. FlockTank uses revenue-based financing, a debt-style investment—you receive repayments from business revenue, not equity or ownership in the co-op. All investments are made through our SEC-compliant platform.",
  },
  {
    question: "What returns can I expect?",
    answer:
      "FlockTank campaigns use revenue-based financing, a debt-style investment. You'll receive proportional payments as the co-op generates revenue until your agreed return is reached. Investors do not receive equity or ownership in the co-op. Returns depend on the success of the business and vary by campaign. All investments carry risk, including the potential loss of your investment.",
  },
  {
    question: "What happens if a campaign doesn't reach its goal?",
    answer:
      "We use an all-or-nothing model. If a campaign doesn't reach its funding goal, your investment is returned, less the 7.5% platform fee which is retained by EconChangers to cover operational costs.",
  },
  {
    question: "Are there limits on how much I can invest?",
    answer:
      "Accredited investors have no limits. For everyone else, SEC Regulation CF limits apply: if both your income and net worth are $124,000+, you can invest up to 10% of the higher figure (max $124,000/year). If either is below $124,000, the limit is 5% of the higher figure, with a minimum of $2,500/year for everyone.",
  },
];

export const entrepreneurQuestions: FAQQuestion[] = [
  {
    question: "How do I start a co-op with EconChangers?",
    answer:
      "Start by signing up for a FlockWorker Business plan or higher, then submit a campaign proposal through FlockTank. Our team will review your business plan and help you prepare for crowdfunding. Once approved, you'll launch your campaign and begin raising funds from community investors. Existing co-ops can also sign up for FlockWorker directly, or run a growth campaign through FlockTank to raise capital for expansion.",
  },
  {
    question: "What does FlockWorker cost?",
    answer:
      "FlockWorker offers tiered plans starting at $39/month plus a per-member fee. The Starter plan ($39/mo + $5/member) includes governance, HR, forms & surveys, and training. Professional ($59/mo + $7/member) adds payroll, Business ($79/mo + $12/member) adds inventory and accounting, and Enterprise ($129/mo + $15/member) adds analytics. Business and Enterprise plans also include FlockList marketplace access. Save 17% with annual billing, and volume discounts are available for 50+ members. An optional Payroll Complete add-on ($29/mo + $2/member) provides automated ACH direct deposit and IRS e-filing.",
  },
  {
    question: "What are the core policies I need to follow?",
    answer:
      "All EconChangers Co-Ops commit to three policies: (1) A 50/50 profit split where half of net profits are shared equally among all workers, (2) Job security commitments protecting workers from technology-related layoffs, and (3) Public transparency through published financial and staffing data.",
  },
  {
    question: "Can an existing business convert to a co-op?",
    answer:
      "Yes! Many successful co-ops started as traditional businesses. We can help you transition to worker ownership while maintaining business continuity. Our team provides guidance on legal structure, governance, and worker education.",
  },
  {
    question: "What are FlockTank growth campaigns?",
    answer:
      "Growth campaigns are for established cooperatives that want to scale. Whether you need to hire new worker-owners, open additional locations, upgrade equipment, or invest in technology, a growth campaign lets you raise capital from community investors. The process follows the same four steps as launch campaigns—submit, review, go live, get funded—but your existing revenue history gives investors more confidence.",
  },
];

export const businessOwnerQuestions: FAQQuestion[] = [
  {
    question: "I'm retiring—can I sell my business to my employees through FlockTank?",
    answer:
      "Absolutely. FlockTank is designed for exactly this. You launch a crowdfunding campaign where community investors provide the capital for your employees to buy the business. You receive a fair price for your life's work, your employees gain ownership, and investors receive revenue-based repayments from the new co-op.",
  },
  {
    question: "How does a business conversion campaign work?",
    answer:
      "First, we help you establish a fair valuation for your business. Then you launch a FlockTank campaign with a funding goal that covers the buyout amount. Community investors contribute, and once the goal is met, the funds go toward purchasing the business. Your employees become worker-owners, and the business transitions to an EconChangers Co-Op with our core policies in place.",
  },
  {
    question: "What happens to my employees during the transition?",
    answer:
      "Your employees stay on and become the new owners. We provide training on cooperative governance, democratic decision-making, and using FlockWorker for business operations. The transition is designed to be smooth—operations continue while ownership transfers to the workers.",
  },
  {
    question: "Do I get paid upfront or over time?",
    answer:
      "Once the FlockTank campaign reaches its funding goal, you receive the agreed-upon sale price. Investors are then repaid over time through the co-op's revenue, not from your proceeds. You exit with your capital while your employees build on what you created.",
  },
  {
    question: "What if my business isn't ready to become a co-op?",
    answer:
      "We offer an Exit Strategy Report powered by Zolidar that evaluates your business and provides a roadmap. This helps you understand what's needed—whether it's financial preparation, employee training, or operational changes—before launching a campaign.",
  },
  {
    question: "Why sell to employees instead of a competitor or private equity?",
    answer:
      "Selling to employees preserves your legacy. The business stays local, jobs are protected, and the people who helped build it become its owners. Unlike private equity, worker ownership means decisions are made by people invested in the community, not distant shareholders focused only on short-term profits.",
  },
];

export const technicalQuestions: FAQQuestion[] = [
  {
    question: "Is my investment protected?",
    answer:
      "All investments are made through our SEC-compliant Reg CF platform with proper legal documentation. While we vet campaigns and require detailed business plans, all investments carry risk, including the potential loss of your principal. If a campaign doesn't reach its funding goal, your investment is returned less the 7.5% platform fee.",
  },
  {
    question: "How do repayments work?",
    answer:
      "Co-ops repay investors through revenue-based financing (RBF), a debt-style arrangement. A percentage of the co-op's gross revenue goes to investors each month until the agreed-upon return is reached. Investors receive repayments, not equity or ownership in the co-op. Payments scale with the business's success.",
  },
  {
    question: "What data does EconChangers collect?",
    answer:
      "We collect standard account information for compliance purposes. For co-ops using FlockWorker, we process business data to provide our services. All EconChangers Co-Ops publish public dashboards with financial and staffing information as part of our transparency commitment.",
  },
];

export const allFAQQuestions: FAQQuestion[] = [
  ...generalQuestions,
  ...investorQuestions,
  ...entrepreneurQuestions,
  ...businessOwnerQuestions,
  ...technicalQuestions,
];

export const questionsByCategory: Record<string, FAQQuestion[]> = {
  general: generalQuestions,
  investors: investorQuestions,
  entrepreneurs: entrepreneurQuestions,
  "business-owners": businessOwnerQuestions,
  technical: technicalQuestions,
};

export const categories = [
  { id: "general", label: "General", shortLabel: "General", icon: "🌐" },
  { id: "investors", label: "For Investors", shortLabel: "Investors", icon: "💰" },
  { id: "entrepreneurs", label: "For Entrepreneurs", shortLabel: "Entrepreneurs", icon: "🚀" },
  { id: "business-owners", label: "For Business Owners", shortLabel: "Owners", icon: "🏢" },
  { id: "technical", label: "Technical", shortLabel: "Technical", icon: "⚙️" },
];
